/**
 * Wire format for a tag-affinity vector on a GET request.
 *
 * The home page reads the visitor's in-session interest vector from storage
 * and sends its head to /api/search/home-showcases, so each row is ordered by
 * overlap on the server the same way For You rows are. Tags carry spaces,
 * ampersands and slashes ("Shirts & Tops", "Gold/White"), so a comma-joined
 * list would need escaping anyway; a JSON array of [tag, weight] pairs is
 * unambiguous and URL-encodes cleanly.
 *
 * Decoding is defensive on purpose: this arrives from the browser. Anything
 * malformed decodes to an EMPTY vector, which the relevance script treats as
 * exactly neutral -- a bad parameter degrades to the unpersonalised page, it
 * never fails the request.
 */

import type { AffinityTag } from './elasticsearch/relevance-weights';

/** Enough to carry a whole session's distinct tags (median 3-5) with room to spare. */
export const AFFINITY_PARAM_MAX_TAGS = 24;
/** Longest tag the vector will carry; approved tags are far shorter. */
export const AFFINITY_PARAM_MAX_TAG_LENGTH = 64;

export function encodeAffinityParam(tags: AffinityTag[] | null | undefined): string | null {
  const pairs = decodeAffinityPairs((tags || []).map(t => [t.tag, t.weight]));
  if (pairs.length === 0) return null;
  return JSON.stringify(pairs.map(t => [t.tag, t.weight]));
}

export function decodeAffinityParam(raw: string | null | undefined): AffinityTag[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return decodeAffinityPairs(parsed);
}

function decodeAffinityPairs(pairs: unknown[]): AffinityTag[] {
  const out: AffinityTag[] = [];
  for (const pair of pairs) {
    if (out.length >= AFFINITY_PARAM_MAX_TAGS) break;
    if (!Array.isArray(pair) || pair.length !== 2) continue;
    const [tag, weight] = pair as [unknown, unknown];
    if (typeof tag !== 'string') continue;
    const trimmed = tag.trim();
    if (!trimmed || trimmed.length > AFFINITY_PARAM_MAX_TAG_LENGTH) continue;
    const w = Number(weight);
    if (!Number.isFinite(w) || w <= 0) continue;
    out.push({ tag: trimmed, weight: w });
  }
  return out;
}
